/*
 * XheyClaw AI 后端调用封装
 *
 * 直连后端各环境域名（不再走 /xhey-ai 同源代理）：
 *   - base 为编译期注入全局 __XHEY_AI_BASE__（vite.config ENV_CONFIG.xheyClawApiHost + /api）
 *   - 鉴权走 Authorization: Bearer（tokenService），跨域可用；需后端放行 CORS
 *
 * 协议对齐 XHeyClaw-Web (已跑通)：
 *   - 不需要「创建会话」接口，前端自己 crypto.randomUUID() 当 conversationId
 *   - chat 接口 POST <base>/chat，body 是 OpenAI 风格 messages
 *   - Header 必带：x-group-id + x-conversation-id
 */

import { store } from '../../app/store'
import type {
  ExportedFile,
  Template,
  SessionItem,
  HistoryMessage,
  SkillItem,
  SkillDetail,
  SkillReferenceItem,
  FileItem
} from './types'
import {
  getValidToken,
  invalidateToken,
  getCachedTokenSync
} from './auth/tokenService'
import { installGroupWatcher } from './auth/groupWatcher'

// 模块加载时安装 group/user 切换监听（清 token），全局只装一次
installGroupWatcher()

// 后端 base：编译期注入的直连域名 + /api（唯一真相源在 vite.config ENV_CONFIG.xheyClawApiHost）
const BASE = __XHEY_AI_BASE__
const CONV_KEY = 'aiClerk:conversationId'

interface ApiResponse<T = unknown> {
  code: number
  data: T
  message?: string
  msg?: string
}

// ─── 把后端给的绝对 API URL 改写到当前环境的直连域名 ──
// 后端 upload 响应里的 refreshUrl 可能带内网/网关 host，形如：
//   http://cpu.xhey.top:10023/api/files/raw-signed?path=...&token=...
// 统一改写成 <XHEY_AI_BASE>/files/raw-signed?...，直连当前环境后端域名，
// 避免命中已废弃的内网地址或撞跨域。
function relayifyApiUrl(url?: string): string | undefined {
  if (!url) return url
  const m = url.match(/^https?:\/\/[^/]+\/api\/(.*)$/)
  if (m) return `${BASE}/${m[1]}`
  return url
}

// ─── 当前 groupId（从 redux store） ─────────────────
function getGroupId(): string {
  try {
    const state: any = store.getState()
    const group = state?.group
    if (!group) return ''
    if (typeof group.get === 'function') return group.get('groupId') || ''
    return group.groupId || ''
  } catch {
    return ''
  }
}

// ─── 当前团队短 id（即 group.invite_id，对应后端的 team_no） ─────────
function getTeamNo(): string {
  try {
    const state: any = store.getState()
    const group = state?.group
    if (!group) return ''
    if (typeof group.get === 'function') return group.get('invite_id') || ''
    return group.invite_id || ''
  } catch {
    return ''
  }
}

// ─── 当前 conversationId（前端生成，存 localStorage）──
export function getOrCreateConversationId(): string {
  try {
    const stored = window.localStorage.getItem(CONV_KEY)
    if (stored) return stored
  } catch {
    /* localStorage 不可用 */
  }
  const newId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36)
  try {
    window.localStorage.setItem(CONV_KEY, newId)
  } catch {
    /* 忽略 */
  }
  return newId
}

/** 强制开启新会话（清掉旧 conversationId） */
export function resetConversation(): string {
  try {
    window.localStorage.removeItem(CONV_KEY)
  } catch {
    /* 忽略 */
  }
  return getOrCreateConversationId()
}

// ─── 通用 headers ───────────────────────────────────
async function buildHeaders(
  extra?: Record<string, string>
): Promise<Record<string, string>> {
  const groupId = getGroupId()
  const headers: Record<string, string> = { ...(extra || {}) }
  if (groupId) headers['x-group-id'] = groupId
  // 注入 cpu Bearer
  try {
    const token = await getValidToken()
    if (token) headers['Authorization'] = `Bearer ${token}`
  } catch (e) {
    // 拿不到 token，请求大概率会 401，由调用方感知
    // eslint-disable-next-line no-console
    console.warn('[xhey-ai] getValidToken 失败', e)
  }
  return headers
}

// ─── 通用 fetch ────────────────────────────────────
type FetchInit = Parameters<typeof fetch>[1]

async function rawFetch(
  path: string,
  init: FetchInit,
  userHeaders: Record<string, string>
): Promise<Response> {
  const headers = await buildHeaders({
    'Content-Type': 'application/json',
    ...userHeaders
  })
  return fetch(`${BASE}${path}`, {
    credentials: 'include',
    ...(init || {}),
    headers
  })
}

async function request<T = unknown>(
  path: string,
  init: FetchInit = {}
): Promise<T> {
  const userInit = init || {}
  const userHeaders =
    (userInit && (userInit as { headers?: Record<string, string> }).headers) ||
    {}

  let res = await rawFetch(path, userInit, userHeaders)

  // 401 → token 失效。清缓存重换一次重试
  if (res.status === 401) {
    // eslint-disable-next-line no-console
    console.warn('[xhey-ai] 401 → 重新换 token 后重试一次:', path)
    invalidateToken()
    res = await rawFetch(path, userInit, userHeaders)
  }

  if (!res.ok) {
    throw new Error(`HTTP ${res.status}: ${await res.text()}`)
  }
  // 后端有些接口直接返回数据，有些包了 {code, data}
  const json = await res.json()
  if (typeof json === 'object' && json !== null && 'code' in json) {
    const wrapped = json as ApiResponse<T>
    if (wrapped.code !== 0 && wrapped.code !== 200) {
      throw new Error(
        wrapped.message || wrapped.msg || `Backend error ${wrapped.code}`
      )
    }
    return wrapped.data
  }
  return json as T
}

// ─── 积分余额（GET /api/credits/balance）────────────────────────
/**
 * 单次填表预估消耗（积分）。统一用于：拦截阈值（available < 它）、积分不足弹窗
 * 「本次消耗」、入口「约可填写 N 次」、卡片「不足 1 次」判断。
 * TODO: 当前为占位值（10 万），待产品 / 后端确认真实单次消耗后改这一处即可。
 */
export const SINGLE_QUERY_COST = 100000

export interface CreditsBalance {
  /** 当前可用积分余额（能花的） */
  available: number | null
  /** 已消耗积分（基础包本月 + 充值包累计） */
  consumed: number | null
  /** 总额度 = consumed + available */
  total_quota: number | null
  /** 钱包到期时间 */
  expire_at: string | null
  /** 会话累计消耗块（传了 session_id 才有） */
  session: {
    session_id: string
    points_consumed: number
    record_count: number
  } | null
  /** 是否新用户首次访问（接口字段；当前导出会员引导按「是否会员」判断，未用本字段） */
  is_first_visit?: boolean
}

/**
 * 查当前登录用户的积分余额；可选附带某会话累计消耗。
 * 计费故障 / 异常一律返回 null（按「余额暂不可用」处理，调用方别当 0）。
 */
export async function fetchCreditsBalance(
  sessionId?: string
): Promise<CreditsBalance | null> {
  try {
    const path = sessionId
      ? `/credits/balance?session_id=${encodeURIComponent(sessionId)}`
      : '/credits/balance'
    return await request<CreditsBalance>(path, { method: 'GET' })
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[xhey-ai] fetchCreditsBalance 失败', e)
    return null
  }
}

// ─── 用户消息 → assistant-ui 格式（对齐 XHeyClaw-Web）────────────
//   每条消息要带 metadata.custom + 一个 nanoid 风格随机 message id
function genMessageId(): string {
  // 16 位 alphanumeric，对齐 XHeyClaw-Web 抓包样本（如 "2xO5wnrS6hnD4XN6"）
  const part = () => Math.random().toString(36).slice(2, 10)
  return (part() + part()).slice(0, 16)
}

interface AssistantUIMessage {
  id: string
  role: 'user' | 'assistant'
  parts: Array<{ type: string; [k: string]: unknown }>
  metadata: { custom: Record<string, unknown> }
}

/** 给历史消息补齐 assistant-ui 必需字段（id / metadata） */
function decorateHistoryMessage(m: {
  role: 'user' | 'assistant'
  parts: Array<{ type: string; [k: string]: unknown }>
}): AssistantUIMessage {
  return {
    id: genMessageId(),
    role: m.role,
    parts: m.parts,
    metadata: { custom: {} }
  }
}

// ─── API ─────────────────────────────────────────────
export const xheyAi = {
  // 上传文件 — 走 /api/platform/upload，自动 .doc/.xls/.ppt → OOXML 转换，返回 OSS 公开 URL
  // 响应：{ originalUrl, url, refreshUrl, filename, converted, path }
  //   - url 是工作副本（AI 可改），originalUrl 是只读基线副本
  //   - refreshUrl 是左侧预览专用 URL（带 query 缓存破坏，每次刷新都拿最新）
  //   - path 是后端 LLM 容器内的本地文件路径（如 "uploads/xxx.xlsx"），让 AI 不
  //     用先 download_oss 直接 read 该路径
  //
  // 三种调用形态：
  //   1. 传 file（本地上传入口）= 上传用户文档，转 OOXML 后返回工作副本
  //   2. 不传 file，但传 templateHint（快捷入口 chip）= 后端按 fileName + fileType
  //      从 OSS 已预生成的模板库里匹配一份对应模板作为工作副本返回；匹配不到再回退空白
  //   3. 都不传（一句话描述生成入口）= 后端按"空白文档"处理，返回一份新建空白文档链接
  // templateHint.fileName = 入口名（chip.label，如"施工进度日报"，对映射表 entryName 列匹配）
  // templateHint.fileType = 'excel' | 'word'（配合 entryName 唯一定位一份模板文件）
  // 注：英文 taskName 不在 upload 传，只在首条会话里隐藏发给 LLM（见 work 页 <aiclerk-task-name>）。
  upload: async (
    file?: File,
    templateHint?: { fileName: string; fileType: 'excel' | 'word' }
  ): Promise<{
    fileUrl: string
    fileName: string
    /** 工作副本 id, 后续重命名要用这个(PATCH /files/{working_id}) */
    workingId?: string
    originalUrl?: string
    converted?: boolean
    filePath?: string
    refreshUrl?: string
  }> => {
    const doUpload = async () => {
      const fd = new FormData()
      // 描述生成入口不传 file:FormData 为空,后端据此返回空白文档链接
      if (file) fd.append('file', file)
      // 快捷入口 chip:不传 file,改传预生成模板的匹配键(入口名 + excel/word),
      // 后端据此从 OSS 拉对应模板当工作副本;匹配不到再回退空白文档。
      if (!file && templateHint) {
        fd.append('fileName', templateHint.fileName)
        fd.append('fileType', templateHint.fileType)
      }
      // upload 不能带 Content-Type，让浏览器自己加 multipart boundary
      const headers = await buildHeaders()
      delete headers['Content-Type']
      return fetch(`${BASE}/platform/upload`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
        headers
      })
    }
    let res = await doUpload()
    if (res.status === 401) {
      invalidateToken()
      res = await doUpload()
    }
    if (!res.ok)
      throw new Error(`Upload failed: ${res.status} ${await res.text()}`)
    const raw = await res.json()
    const fileUrl: string = raw.url || raw.data?.url
    if (!fileUrl) {
      throw new Error(
        `Upload response missing url field: ${JSON.stringify(raw)}`
      )
    }
    return {
      fileUrl,
      // 优先 original_name(AI/用户命名),兜底 filename 物理名 → 本地 file 名。
      // rename 时会改这个字段;path 不变、fileUrl 保持不变。
      fileName:
        raw.original_name || raw.filename || raw.fileName || file?.name || '',
      workingId: raw.working_id,
      originalUrl: raw.originalUrl,
      converted: raw.converted,
      filePath: raw.path,
      // refreshUrl 后端给的是绝对 URL（可能带内网/网关 host），改写到当前环境
      // 的直连域名 <XHEY_AI_BASE>/...，避免命中已废弃内网地址或撞 mixed-content。
      refreshUrl: relayifyApiUrl(raw.refreshUrl)
    }
  },

  /**
   * 重命名文档显示名 —— PATCH /api/platform/files/{working_id}
   * 只改 original_name; path/fileUrl/refreshUrl 均不变(后端 signed URL 基于 path)。
   * name 可带或不带扩展名,后端会强制保留文件真实扩展。撞名自动追加 " (2)"。
   * 只有从 upload 拿到 working_id 的文件能用;老列表文件(listRecentFiles)没 working_id 无法改名。
   */
  renameDocument: async (
    workingId: string,
    name: string
  ): Promise<{ workingId: string; originalName: string; path: string }> => {
    const doRename = async () => {
      const headers = await buildHeaders()
      headers['Content-Type'] = 'application/json'
      return fetch(`${BASE}/platform/files/${encodeURIComponent(workingId)}`, {
        method: 'PATCH',
        body: JSON.stringify({ name }),
        credentials: 'include',
        headers
      })
    }
    let res = await doRename()
    if (res.status === 401) {
      invalidateToken()
      res = await doRename()
    }
    if (!res.ok) {
      let detail = `Rename failed: ${res.status}`
      try {
        const body = await res.json()
        if (body && body.detail) detail = String(body.detail)
      } catch {
        /* 忽略 */
      }
      throw new Error(detail)
    }
    const raw = await res.json()
    return {
      workingId: raw.working_id || workingId,
      originalName: raw.original_name || '',
      path: raw.path || ''
    }
  },

  // ─── Composer 附件上传（图片 / 文档通吃） ──────────────
  // /api/upload 比 /api/platform/upload 宽：接受图片 / PDF / Office / 文本，
  // 不做 .doc/.xls 的 OOXML 转换（composer 附件只是参考资料，不用转）。
  // 响应：{ url: "..." }（XHeyClaw-Web/lib/oss-attachment-adapter.ts 同款）
  uploadAttachment: async (
    file: File
  ): Promise<{ fileUrl: string; fileName: string }> => {
    const doUpload = async () => {
      const fd = new FormData()
      fd.append('file', file)
      const headers = await buildHeaders()
      // multipart 必须让浏览器自己加 boundary
      delete headers['Content-Type']
      return fetch(`${BASE}/upload`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
        headers
      })
    }
    let res = await doUpload()
    if (res.status === 401) {
      invalidateToken()
      res = await doUpload()
    }
    if (!res.ok) {
      let detail = `Upload failed: ${res.status}`
      try {
        const body = await res.json()
        if (body && body.detail) detail = String(body.detail)
      } catch {
        /* 忽略 */
      }
      throw new Error(detail)
    }
    const raw = await res.json()
    // 老版本后端：{ url }；新版本：{ path, filename, original_name, ... }
    // path 没有自带 URL → 走 /api/files/raw?path=... 拼一个可访问 URL（同源代理，
    // 跟 home 列表里"下载"用的是同一条路径）。
    let fileUrl: string = raw.url || raw.data?.url || ''
    if (!fileUrl && typeof raw.path === 'string' && raw.path) {
      const qs = `path=${encodeURIComponent(raw.path)}`
      fileUrl = `${BASE}/files/raw?${qs}`
    }
    if (!fileUrl)
      throw new Error(
        `Upload response missing url/path field: ${JSON.stringify(raw)}`
      )
    return {
      fileUrl,
      fileName: raw.original_name || raw.filename || file.name
    }
  },

  listExportedFiles: () => request<ExportedFile[]>('/files'),

  // ─── 会话管理（对齐 Platform Shared Instance /api/sessions） ────
  //   GET /api/sessions?limit&offset → { sessions: [{id, title, updated_at, message_count}] }
  //   GET /api/sessions/{id}/history?limit
  //   PATCH /api/sessions/{id} { name }
  //   DELETE /api/sessions/{id}
  listSessions: async (params?: {
    limit?: number
    offset?: number
  }): Promise<SessionItem[]> => {
    const qs: string[] = []
    if (params?.limit != null) qs.push(`limit=${params.limit}`)
    if (params?.offset != null) qs.push(`offset=${params.offset}`)
    const path = `/sessions${qs.length ? `?${qs.join('&')}` : ''}`
    const raw = await request<any>(path)
    if (Array.isArray(raw)) return raw as SessionItem[]
    if (raw && Array.isArray(raw.sessions)) return raw.sessions as SessionItem[]
    return []
  },

  /** 取单个 session 的 context_usage —— 对齐 XHeyClaw-Web/lib/use-session-context-usage.ts:
   *    GET /api/sessions/{id}/context-usage?group_id=<gid>  → { context_usage: {...} | null }
   *  (横杠 context-usage,不是下划线;单 session 子路径,不是 list 全部 sessions 再匹配) */
  getSessionContextUsageRaw: async (
    sessionId: string,
    signal?: AbortSignal
  ): Promise<any> => {
    const groupIdFull = getGroupId()
    const qs: string[] = []
    if (groupIdFull) qs.push(`group_id=${encodeURIComponent(groupIdFull)}`)
    const path = `/sessions/${encodeURIComponent(sessionId)}/context-usage${
      qs.length ? `?${qs.join('&')}` : ''
    }`
    const init: FetchInit = signal ? { signal } : {}
    return request<any>(path, init)
  },

  getSessionHistory: async (
    sessionId: string,
    params?: { limit?: number }
  ): Promise<HistoryMessage[]> => {
    // 对齐 XHeyClaw-Web/lib/use-chat-persistence.ts：
    //   GET /api/sessions/{id}/history?group_id=<gid>&limit=200
    // group_id 既走 header（buildHeaders 会注入 x-group-id），也走 query；
    // 后端两种取法都兼容，跟源站一致。
    const qs: string[] = [`limit=${params?.limit ?? 200}`]
    const groupIdFull = getGroupId()
    if (groupIdFull) qs.push(`group_id=${encodeURIComponent(groupIdFull)}`)
    const path = `/sessions/${encodeURIComponent(sessionId)}/history?${qs.join(
      '&'
    )}`
    const raw = await request<any>(path)
    let list: HistoryMessage[] = []
    if (Array.isArray(raw)) list = raw as HistoryMessage[]
    else if (raw && Array.isArray(raw.messages))
      list = raw.messages as HistoryMessage[]
    else if (raw && Array.isArray(raw.history))
      list = raw.history as HistoryMessage[]
    return list
  },

  renameSession: (sessionId: string, name: string) =>
    request<unknown>(`/sessions/${encodeURIComponent(sessionId)}`, {
      method: 'PATCH',
      body: JSON.stringify({ name })
    }),

  deleteSession: (sessionId: string) =>
    request<unknown>(`/sessions/${encodeURIComponent(sessionId)}`, {
      method: 'DELETE'
    }),

  // ─── 我的模板库（对齐 /api/doc-templates） ────
  listUserSkills: async (): Promise<SkillItem[]> => {
    const raw = await request<any>('/doc-templates')
    if (Array.isArray(raw)) return raw as SkillItem[]
    if (raw && Array.isArray(raw.skills)) return raw.skills as SkillItem[]
    return []
  },

  /** 拿单个技能详情（编辑 Modal 不展示 content body，只用 display_name + description） */
  getUserSkill: async (name: string): Promise<SkillDetail> => {
    const raw = await request<any>(`/doc-templates/${encodeURIComponent(name)}`)
    return (raw && raw.skill ? raw.skill : raw) as SkillDetail
  },

  /** 改技能 frontmatter 的 display_name / description / summary（保留 SKILL.md body 不动）
   *  summary = 用户业务描述; description = LLM 触发判断 (一般不让用户改)
   */
  updateUserSkill: async (
    name: string,
    body: {
      display_name?: string
      description?: string
      summary?: string
    }
  ): Promise<SkillItem> => {
    const raw = await request<any>(
      `/doc-templates/${encodeURIComponent(name)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(body)
      }
    )
    return (raw && raw.skill ? raw.skill : raw) as SkillItem
  },

  /** 列技能 references/ 文件，relayify download/preview URL 走同源代理 */
  listSkillReferences: async (name: string): Promise<SkillReferenceItem[]> => {
    const raw = await request<any>(
      `/doc-templates/${encodeURIComponent(name)}/references`
    )
    const list: SkillReferenceItem[] = Array.isArray(raw)
      ? (raw as SkillReferenceItem[])
      : Array.isArray(raw?.references)
        ? (raw.references as SkillReferenceItem[])
        : []
    return list.map((r) => ({
      ...r,
      download_url: relayifyApiUrl(r.download_url),
      preview_url: relayifyApiUrl(r.preview_url)
    }))
  },

  /** 删 skill 整目录 (含 SKILL.md + references/). 不可恢复. */
  deleteUserSkill: async (name: string): Promise<void> => {
    await request<any>(`/doc-templates/${encodeURIComponent(name)}`, {
      method: 'DELETE'
    })
  },

  /**
   * 「继续使用」两种 skill 路径:
   *   - 有模板 (references 含可填表文件): 服务端拷贝到 user uploads/ 当 working,
   *     返跟手动上传一致的 file 字段. 防 Agent 改坏 skill 自带原始文件.
   *   - 无模板 (查询/纯文字型 skill): 仅返激活元信息, 前端不 setFile,
   *     work 页用 skill 触发词进 chat-only 模式, LLM 按 SKILL.md 主动开题.
   */
  useSkill: async (
    name: string
  ): Promise<
    | {
        hasTemplate: true
        skill: { name: string; display_name: string }
        fileUrl: string
        fileName: string
        filePath?: string
        refreshUrl?: string
      }
    | {
        hasTemplate: false
        skill: { name: string; display_name: string }
      }
  > => {
    const raw = await request<any>(
      `/doc-templates/${encodeURIComponent(name)}/use`,
      { method: 'POST' }
    )
    const skill = raw.skill || { name, display_name: name }
    if (raw.has_template === false) {
      return { hasTemplate: false, skill }
    }
    const fileUrl: string = raw.url || raw.download_url
    if (!fileUrl) {
      throw new Error(
        `useSkill response missing url field: ${JSON.stringify(raw)}`
      )
    }
    return {
      hasTemplate: true,
      skill,
      fileUrl: relayifyApiUrl(fileUrl) || fileUrl,
      fileName: raw.original_name || raw.filename || `${name}.xlsx`,
      filePath: raw.path,
      refreshUrl: relayifyApiUrl(raw.refreshUrl)
    }
  },

  /** 上传 / 替换 reference 文件（同名覆盖）。multipart/form-data。 */
  uploadSkillReference: async (
    name: string,
    file: File
  ): Promise<SkillReferenceItem> => {
    const doUpload = async () => {
      const fd = new FormData()
      fd.append('file', file)
      const headers = await buildHeaders()
      delete headers['Content-Type']
      return fetch(
        `${BASE}/doc-templates/${encodeURIComponent(name)}/references`,
        {
          method: 'POST',
          body: fd,
          credentials: 'include',
          headers
        }
      )
    }
    let res = await doUpload()
    if (res.status === 401) {
      invalidateToken()
      res = await doUpload()
    }
    if (!res.ok) {
      let detail = `upload reference failed: ${res.status}`
      try {
        const body = await res.json()
        if (body && body.detail) detail = String(body.detail)
      } catch {
        /* 忽略 */
      }
      throw new Error(detail)
    }
    const raw = await res.json()
    const ref = (
      raw && raw.reference ? raw.reference : raw
    ) as SkillReferenceItem
    return {
      ...ref,
      download_url: relayifyApiUrl(ref.download_url),
      preview_url: relayifyApiUrl(ref.preview_url)
    }
  },

  // ─── 技能 name → display_name 字典（对齐 /api/skills） ────
  // ToolCallInline 用，把 raw slug 翻译成中文名展示
  listSkills: async (
    groupId: string
  ): Promise<Array<{ name: string; display_name?: string }>> => {
    if (!groupId) return []
    const raw = await request<any>(
      `/skills?group_id=${encodeURIComponent(groupId)}`
    )
    if (Array.isArray(raw)) return raw
    if (raw && Array.isArray(raw.skills)) return raw.skills
    return []
  },

  // ─── 最近填好的文档（对齐 /api/files） ────
  listRecentFiles: async (params?: {
    prefix?: string
    limit?: number
  }): Promise<FileItem[]> => {
    const qs: string[] = []
    if (params?.prefix) qs.push(`prefix=${encodeURIComponent(params.prefix)}`)
    const path = `/files${qs.length ? `?${qs.join('&')}` : ''}`
    const raw = await request<any>(path)
    // download_url / preview_url 是 server 拼的绝对 URL（可能带内网 host）.
    // HTTPS web 直接用异源 host 会撞 mixed content / 跨域 → "点了没反应".
    // relayify 改写到当前环境直连域名 (<XHEY_AI_BASE>/...), 统一走对的后端.
    const relayifyFile = (f: FileItem): FileItem => ({
      ...f,
      download_url: relayifyApiUrl(f.download_url),
      preview_url: relayifyApiUrl(f.preview_url)
    })
    if (Array.isArray(raw)) return (raw as FileItem[]).map(relayifyFile)
    if (raw && Array.isArray(raw.files)) {
      const list = (raw.files as FileItem[]).map(relayifyFile)
      // 后端没分页就在前端裁
      if (params?.limit) return list.slice(0, params.limit)
      return list
    }
    return []
  },

  /** 拼接 /api/files/raw 下载 / 预览 URL */
  buildFileRawUrl: (params: { path: string; download?: boolean }): string => {
    const qs: string[] = [`path=${encodeURIComponent(params.path)}`]
    if (params.download) qs.push('download=true')
    return `${BASE}/files/raw?${qs.join('&')}`
  },

  listTemplates: () => request<Template[]>('/templates'),

  // saveAsTemplate 已下线 — 资料员"保存模板"现在由 Agent 在填表完成时自决主动
  // 提议, 用户回应后 LLM 自己调 register_team_skill tool (一次完整的会话工具调用),
  // 前端不再提供硬入口 / REST facade.

  // ─── 对话（SSE）─────────────────────────────────
  // 直接调真后端 /api/chat（assistant-ui parts 格式 + OpenAI SSE 响应）
  chatStream: async (params: {
    /** 历史消息（不含本轮新消息），格式参照 XHeyClaw-Web */
    history?: Array<{
      role: 'user' | 'assistant'
      parts: Array<{ type: string; [k: string]: unknown }>
    }>
    /** 本轮新发的用户文本 */
    userText: string
    /** composer 上传的结构化附件（图片/文档），对齐 XHeyClaw-Web file part */
    attachments?: Array<{ url: string; name: string; mediaType: string }>
    /**
     * 主文档在后端 LLM 容器内的本地路径（如 "uploads/xxx.xlsx"）。
     * **只在新会话首条消息**传，注入到文本里告诉模型："本地文件路径: xxx"，
     * 让 AI 直接 read 而不用 download_oss。后续轮次不传，省 token / 防误读。
     */
    currentDocPath?: string
  }): Promise<Response> => {
    const conversationId = getOrCreateConversationId()

    // 1. composer 附件（input box 上传，需要在用户气泡里展示缩略图 / 文件卡）
    type Att = { url: string; name: string; mediaType: string }
    const composerAtts: Att[] = []
    if (params.attachments) {
      for (const a of params.attachments) {
        if (!composerAtts.find((x) => x.url === a.url)) composerAtts.push(a)
      }
    }

    // 2. 用户消息 parts：text + 每个 composer 附件一个 file part
    //    主文档不放 file part（不显示在气泡里），后端通过文本里的 <current-doc> 段拿
    const userParts: AssistantUIMessage['parts'] = [
      { type: 'text', text: params.userText }
    ]
    for (const a of composerAtts) {
      userParts.push({
        type: 'file',
        url: a.url,
        mediaType: a.mediaType,
        name: a.name
      })
    }

    // 3. 文本注入：
    //    <message-attachments> ... </message-attachments>  composer 上传，气泡内展示
    //    <current-doc>         ... </current-doc>          主文档本地路径，仅首条消息
    const textPart = userParts[0] as { type: 'text'; text: string }
    const segs: string[] = [textPart.text]
    if (composerAtts.length > 0) {
      const lines: string[] = ['<message-attachments>']
      for (const a of composerAtts) {
        if (a.mediaType.startsWith('image/')) {
          lines.push(`![${a.name}](${a.url})`)
        } else {
          lines.push(`[${a.name}](${a.url})`)
        }
      }
      lines.push('</message-attachments>')
      segs.push('\n\n' + lines.join('\n'))
    }
    if (params.currentDocPath) {
      segs.push(
        '\n\n<current-doc>\n' +
          `本地文件路径: ${params.currentDocPath}\n` +
          '</current-doc>'
      )
    }
    textPart.text = segs.join('').replace(/^\n+/, '')

    const newUserMessage: AssistantUIMessage = {
      id: genMessageId(),
      role: 'user',
      parts: userParts,
      metadata: { custom: {} }
    }

    // 顶层 body 字段对齐 XHeyClaw-Web 抓包样本：
    //   { tools: {}, id: "DEFAULT_THREAD_ID", messages, trigger, metadata }
    const messages: AssistantUIMessage[] = [
      ...(params.history || []).map(decorateHistoryMessage),
      newUserMessage
    ]
    const body = {
      tools: {},
      id: 'DEFAULT_THREAD_ID',
      messages,
      trigger: 'submit-message',
      metadata: {}
    }

    // x-team-id = group.invite_id（团队短 id / team_no），从 redux store 取
    const teamNo = getTeamNo()

    const doChat = async () => {
      const headers = await buildHeaders({
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        'x-conversation-id': conversationId,
        // 资料员 lazy provision marker — backend _ensure_team 看到这个会
        // 自动 INSERT/activate suspended team
        'x-app-id': 'aiclerk',
        ...(teamNo ? { 'x-team-id': teamNo } : {})
      })
      return fetch(`${BASE}/chat`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify(body)
      })
    }
    let res = await doChat()
    if (res.status === 401) {
      // eslint-disable-next-line no-console
      console.warn('[xhey-ai] chat 401 → 重换 token 重试')
      invalidateToken()
      res = await doChat()
    }
    if (!res.ok)
      throw new Error(`Chat failed: ${res.status} ${await res.text()}`)
    return res
  },

  // ─── 中止对话 ───────────────────────────────────
  // /api/chat/abort 通过 runId 找到 Server cancel_event.set(), Server 那侧
  // SSE 流停推 + openclaw RPC chat.abort → controller.abort() → agent loop 退出.
  // 不带 runId 时 Server 回 400 — 调用方必须先从 SSE 首个 `event: runId` 事件
  // 抽到 runId 才能调本接口. 网络挂了 throw 出去, 调用方自己决定 swallow 还是 UI.
  chatAbort: async (runId: string): Promise<void> => {
    if (!runId) throw new Error('chatAbort: runId required')
    const headers = await buildHeaders({
      'Content-Type': 'application/json'
    })
    const res = await fetch(`${BASE}/chat/abort`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify({ runId })
    })
    if (!res.ok) {
      // eslint-disable-next-line no-console
      console.warn(`[xhey-ai] chat/abort failed: ${res.status}`)
    }
  },

  // ─── pagehide / unmount 兜底 abort (sync, best-effort) ───
  // 关 tab / 刷新 / 后退 时调. normal fetch 在 unload 期间会被浏览器 cancel,
  // 必须 `keepalive: true` (现代浏览器规范) 才能在 unload 期间发完.
  // sendBeacon 不能加自定义 header (x-group-id / Bearer 缺失会 401/400 — Server
  // verify_request 必须 x-group-id), 故走 fetch+keepalive.
  // token / groupId 从 sync 源取 (无 await), 拿不到 → 后端拒, swallow.
  chatAbortBeacon: (runId: string): void => {
    if (!runId) return
    const groupId = getGroupId()
    const token = getCachedTokenSync()
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    if (groupId) headers['x-group-id'] = groupId
    if (token) headers['Authorization'] = `Bearer ${token}`
    fetch(`${BASE}/chat/abort`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers,
      body: JSON.stringify({ runId })
    }).catch(() => {
      /* unload 时 promise 不一定 resolve, swallow */
    })
  }
}
